/**
 * Authentication utility functions
 * Provides domain detection and authentication helpers
 */

/**
 * Check if an email belongs to an internal domain
 * @param email - Email address to check
 * @returns true if email belongs to an internal domain
 */
export const isInternalDomain = (email: string): boolean => {
  if (!email || !email.includes('@')) {
    return false;
  }

  const internalDomains = (import.meta.env.VITE_INTERNAL_DOMAINS || '@aprco.com')
    .split(',')
    .map(domain => domain.trim())
    .filter(domain => domain.startsWith('@'));

  return internalDomains.some(domain => email.endsWith(domain));
};

/**
 * Extract domain from email address
 * @param email - Email address
 * @returns Domain part of email (e.g., "company.com")
 */
export const getEmailDomain = (email: string): string | null => {
  if (!email || !email.includes('@')) {
    return null;
  }
  return email.split('@')[1]?.toLowerCase() || null;
};

/**
 * Check if Google provider is enabled
 * @returns true if Google sign-in is enabled
 */
export const isGoogleProviderEnabled = (): boolean => {
  return import.meta.env.VITE_GOOGLE_PROVIDER_ENABLED === 'true' || 
         import.meta.env.VITE_IDENTITY_PLATFORM_ENABLED === 'true';
};

/**
 * Check if auto-provisioning is enabled for internal users
 * @returns true if auto-provisioning is enabled
 */
export const isAutoProvisioningEnabled = (): boolean => {
  return import.meta.env.VITE_AUTO_PROVISION_INTERNAL_USERS === 'true';
};

/**
 * Get the list of approved workspace domains for Google One Tap / Workspace sign-in.
 * Derived from VITE_INTERNAL_DOMAINS (e.g. "@aprco.com" or "@aprco.com,@other.com").
 * @returns Array of lowercase domain strings without the @ (e.g. ["aprco.com"])
 */
export const getApprovedWorkspaceDomains = (): string[] => {
  const raw = import.meta.env.VITE_INTERNAL_DOMAINS || '@aprco.com';
  return raw
    .split(',')
    .map((d: string) => d.trim())
    .filter((d: string) => d.startsWith('@'))
    .map((d: string) => d.slice(1).toLowerCase());
};

/**
 * Check if an email belongs to an approved workspace domain (for One Tap).
 * @param email - Email address to check
 * @returns true if the email's domain is in the approved workspace list
 */
export const isApprovedWorkspaceEmail = (email: string): boolean => {
  const domains = getApprovedWorkspaceDomains();
  if (!domains.length || !email || !email.includes('@')) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? domains.includes(domain) : false;
};

